import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./node-details-panel.module.scss";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { Button } from "@components/atoms/buttons/button/button";
import {
  emptyNodeDataObject,
  updateNodeSelectionNodeData,
  updateNodeSelectionState,
  updateStructureWasSaved,
} from "@src/reducers/your-reducer";

import { DataAcquisitionTypeItemsWrapper } from "@components/organisms/data-acquisition-type-items-wrapper/data-acquisition-type-items-wrapper";
import { TimeSeriesLineGraphWrapper } from "@components/molecules/time-series-line-graph-wrapper/time-series-line-graph-wrapper";
import { RenderUnreleasedFeatures } from "@components/molecules/render-unreleased-features/render-unreleased-features";
import { UnitInputAndPopover } from "../unit-input-and-popover/unit-input-and-popover";
import { useMachineAncestorsLineageTreeHook } from "@src/components/molecules/ancestors/ancestors-lineage-tree/use-machine-ancestors-lineage-tree-hook";
import { useInitialAncestorsLineageTreeHook } from "@src/components/molecules/ancestors/ancestors-lineage-tree/use-initial-ancestors-lineage-tree-hook";
import { OurNodeAncestorsLineageTree } from "@src/components/molecules/ancestors/ancestors-lineage-tree/our-node-ancestors-lineage-tree";
import { AttributeNameValue } from "./attribute-name-value/attribute-name-value";
import { NodeIdBlock } from "./node-id-block/node-id-block";
import { getNodeManager } from "@src/cloud-connect/dependency-injection-container/get-node-manager";
import { saveNodeDataInPanel } from "@src/cloud-connect/use-cases/save-node-data-in-panel";
import { getLogger } from "@src/cloud-connect/dependency-injection-container/get-logger";
import { fetchTimeseriesData } from "@src/cloud-connect/use-cases/fetch-timeseries-data";
import { LastActualValueAndTimestampIfJson } from "./last-actual-value-and-timestamp-if-json/last-actual-value-and-timestamp-if-json";
import { LastActualValueAndTimestampIfStringOrNumeric } from "./last-actual-value-and-timestamp-if-string-or-numeric/last-actual-value-and-timestamp-if-string-or-numeric";
import {
  errorSnackbar,
  successSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { getTileCollection } from "@src/cloud-connect/dependency-injection-container/get-tile-collection";

import { Tile } from "@src/cloud-connect/blueprint/dashboards/entities/tile";
import { TimePeriod } from "@src/cloud-connect/blueprint/dashboards/entities/time-period";
import {
  ChartType,
  DashboardTileNode,
  NodeType,
} from "@src/cloud-connect/blueprint/dashboards/entities/dashboard-tile-node";
import { TimeSeriesDataType } from "./time-series-data-type/time-series-data-type";
import { TsidContainer } from "./tsid-container/tsid-container";
import { TimestampsAndStringsDataTable } from "./timestamps-and-strings-data-table/timestamps-and-strings-data-table";
import { useI18N } from "@src/language/language-context";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";

const cssClassName = "node-details-panel";

export const OurNodeDetailsPanel = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const nodeSelectionNodeData = useSelector(
    (state: RootState) => state.yourSlice.nodeSelection.nodeData,
  );
  const nodeSelectionNodeState = useSelector(
    (state: RootState) => state.yourSlice.nodeSelection.state,
  );

  const texts = {
    nameLabel: i18n("modules.ourNodeDetailsPanel.nameLabel"),
    smallestSampleIntervalLabel: i18n(
      "modules.ourNodeDetailsPanel.smallestSampleIntervalLabel",
    ),
    dataTypeLabel: i18n("modules.ourNodeDetailsPanel.dataTypeLabel"),
    nodeIdBlock: {
      tooltip: i18n("modules.ourNodeDetailsPanel.nodeIdBlock.tooltip"),
      child: {
        label: i18n("modules.ourNodeDetailsPanel.nodeIdBlock.child.label"),
      },
      parent: {
        label: i18n("modules.ourNodeDetailsPanel.nodeIdBlock.parent.label"),
      },
    },
    nodeIdSnackBarSuccess: i18n(
      "modules.ourNodeDetailsPanel.nodeIdSnackBarSuccess",
    ),
    nodeIdSnackBarError: i18n(
      "modules.ourNodeDetailsPanel.nodeIdSnackBarError",
    ),
    warningNoEmptyNodeName: i18n(
      "modules.ourNodeDetailsPanel.warningNoEmptyNodeName",
    ),
    unknown: i18n("modules.ourNodeDetailsPanel.unknown"),
    selectTileLabel: i18n("modules.ourNodeDetailsPanel.selectTileLabel"),
    selectChartTypeLabel: i18n(
      "modules.ourNodeDetailsPanel.selectChartTypeLabel",
    ),
    cancelButtonText: i18n("modules.ourNodeDetailsPanel.cancelButtonText"),
    removeButtonText: i18n("modules.ourNodeDetailsPanel.removeButtonText"),
    saveButtonText: i18n("modules.ourNodeDetailsPanel.saveButtonText"),
    timeSeriesDataLabelText: i18n(
      "modules.ourNodeDetailsPanel.timeSeriesDataLabelText",
    ),
    timeSeriesDataUnitLabel: i18n(
      "modules.ourNodeDetailsPanel.timeSeriesDataUnitLabel",
    ),
  };

  const {
    name,
    lastActualValueTimestamp,
    lastActualValue,
    smallestSampleIntervall,
    nodeId,
    parentNodeId,
    key,
    dataAcquisitionType,
    dataType,
    dataTypeAsString,
    unit,
  } = nodeSelectionNodeData;

  const chartTypes = ["bar", "line", "text"];
  const [chartType, setChartType] = useState<ChartType>("line");
  const [localName, setLocalName] = useState(name);
  const [localDataAcquisitionType, setLocalDataAcquisitionType] =
    useState(dataAcquisitionType);
  const [localPollingIntervalTime, setLocalPollingIntervalTime] = useState(0);
  const [localUnit, setLocalUnit] = useState(unit);
  const [graphOptionsObject, setGraphOptionsObject] = useState(undefined);
  const [lastKnownValueAndTimestamp, setLastKnownValueAndTimestamp] =
    useState(undefined);
  const [timeseriesDataType, setTimeseriesDataType] = useState(undefined);
  const [tsid, setTsid] = useState(undefined);
  const [stringDataAndTimestampData, setStringDataAndTimestampData] =
    useState(undefined);

  const prevLocalNameRef = useRef(null);

  const handleChangeChartType = (chartType) => {
    setChartType(chartType);
  };

  const nullPatternTile = new Tile(
    "none",
    "none",
    1,
    "quarter",
    "quarterhourly",
    new TimePeriod(1, "days"),
    [],
    false,
  );

  const [localTiles, setLocalTiles] = useState([]);
  const [localTileId, setLocalTileId] = useState<string>(undefined);
  const [nodeType, setNodeType] = useState<NodeType>("numeric");

  const handleChangeLocalTileId = (tileId) => {
    setLocalTileId(tileId);
  };

  // TODO: remove if not used
  const handleChangeNodeType = (nodeType) => {
    setNodeType(nodeType);
  };

  // TODO: extract to own file
  const saveNodeInTile = () => {
    if (localTileId) {
      // get the nodes from the tile
      let nodes = [];
      let tileToBeUpdated;

      getTileCollection()
        .getTiles()
        .forEach((tile) => {
          if (tile.getId() === localTileId) {
            tileToBeUpdated = tile;
            nodes = tile.getNodes();
          }
        });

      if (localTileId === "none") {
        getTileCollection().removeNodeFromAllTiles(nodeId);
      } else {
        // create a new node object
        getTileCollection().removeNodeFromAllTiles(nodeId);
        const newNode = new DashboardTileNode(
          nodeId,
          localName,
          chartType,
          nodeType,
        );

        // is the node already in the tile?
        if (nodes.find((node) => node.getId() === newNode.getId())) {
          const newNodes = [];
          nodes.forEach((node) => {
            if (node.getId() !== newNode.getId()) {
              newNodes.push(node);
            } else {
              newNodes.push(newNode);
            }
          });
          tileToBeUpdated.setNodes(newNodes);
        } else {
          nodes.push(newNode);
          tileToBeUpdated.setNodes(nodes);
        }
      }
    }
  };

  useEffect(() => {
    setLocalTiles([nullPatternTile, ...getTileCollection().getTiles()]);
    const tileId = getTileCollection().getTileIdIfNodeIdPresent(nodeId);
    const nodeChartType: ChartType =
      getTileCollection().getNodeChartTypeIfNodeIdPresent(nodeId);

    if (tileId && tileId !== "") {
      setLocalTileId(tileId);
      setChartType(nodeChartType);
    } else {
      setLocalTileId("none");
    }
  }, [nodeId]);

  const smallestSampleIntervallInSeconds: string = smallestSampleIntervall
    ? smallestSampleIntervall + " s"
    : texts.unknown;

  const closeNodeDataPanel = () => {
    dispatch(updateNodeSelectionState("none"));
    dispatch(updateNodeSelectionNodeData(emptyNodeDataObject));
  };

  const returnSecondsFromInputAndDropDown = (timeValue: number) => {
    setLocalPollingIntervalTime(timeValue);
  };

  const updateLocalDataAcquisitionType = (returnedArray) => {
    const selectedValue = returnedArray.find((item) => item.selected).value;
    setLocalDataAcquisitionType(selectedValue);
  };

  const saveNodeDataAndCloseDataPanel = (
    nodeId,
    name,
    dataAcquisitionType,
    pollingIntervalTime,
    unit,
  ) => {
    saveNodeDataInPanel(
      nodeId,
      name,
      dataAcquisitionType,
      pollingIntervalTime,
      unit,
    );

    closeNodeDataPanel();
    dispatch(updateStructureWasSaved(false));
  };

  const removeNodeAndCloseDataPanel = () => {
    const logger = getLogger();
    const t0 = performance.now();
    logger.debug("t0 - start of action", t0);

    closeNodeDataPanel();
    const nodeManager = getNodeManager();

    nodeManager
      .deleteNodesFromSidePanelHandler()
      .deleteNodeFromSidePanel(nodeId);

    dispatch(updateStructureWasSaved(false));

    const t1 = performance.now();
    logger.debug("t1 - end of action", t1);
    logger.debug(
      `Action to remove node on new structure from side panel took ${
        t1 - t0
      } milliseconds.`,
    );
  };

  const displayName = (name) => {
    return (
      <label className={styles[`${cssClassName}__label`]}>
        Name
        <input
          type="text"
          value={name}
          className={`
            ${styles[`${cssClassName}__input`]}
            ${styles[`${cssClassName}__input--disable`]}
          `}
          disabled
        />
      </label>
    );
  };

  const displayEditName = (name) => {
    return (
      <label className={styles[`${cssClassName}__label`]}>
        {texts.nameLabel}
        <input
          type="text"
          value={name}
          className={styles[`${cssClassName}__input`]}
          onChange={(e) => setLocalName(e.target.value)}
        />
      </label>
    );
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      successSnackbar(texts.nodeIdSnackBarSuccess);
    } catch (err) {
      errorSnackbar(`${texts.nodeIdSnackBarError}: ${err}`, 5000);
    }
  };

  const copyNodeIdToClipBoard = () => {
    copyToClipboard(nodeId);
  };

  const copyParentNodeIdToClipBoard = () => {
    copyToClipboard(parentNodeId);
  };

  const shortenDisplayedNodeId = (nodeId: string, num = 36): string => {
    if (nodeId.length <= num) {
      return nodeId;
    }
    return nodeId.substring(0, num) + "...";
  };

  const displayAncestorsLineage = () => {
    return (
      <>
        <OurNodeAncestorsLineageTree
          nodeId={nodeId}
          label={i18n("modules.ourNodeDetailsPanel.newLocation")}
          useHook={useMachineAncestorsLineageTreeHook}
        />
        <OurNodeAncestorsLineageTree
          nodeId={nodeId}
          label={i18n("modules.ourNodeDetailsPanel.originalLocation")}
          useHook={useInitialAncestorsLineageTreeHook}
        />
      </>
    );
  };

  const DisplayDataAcquisitionTypeItemsWrapper = useCallback(
    ({ render = true }: { render?: boolean }) => {
      return (
        render &&
        localDataAcquisitionType && (
          <DataAcquisitionTypeItemsWrapper
            updateFunc={updateLocalDataAcquisitionType}
            dataAcquisitionType={localDataAcquisitionType}
            intervalInSeconds={smallestSampleIntervall}
            returnSecondsFromInputAndDropDown={
              returnSecondsFromInputAndDropDown
            }
          />
        )
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [localDataAcquisitionType, smallestSampleIntervall],
  );

  const displayUnitOrNothing = (unit) => {
    return unit !== null && unit.code !== -1 ? (
      <label className={styles[`${cssClassName}__label`]}>
        Unit
        <p
          className={`
            ${styles[`${cssClassName}__input`]}
            ${styles[`${cssClassName}__input--disable`]}
          `}
        >
          {unit.display_name}
        </p>
      </label>
    ) : null;
  };

  const displayInputField = () => {
    return (
      <UnitInputAndPopover
        key={key}
        unit={unit}
        popoverOnClickCallback={setUnitOnNode}
      />
    );
  };

  const setUnitOnNode = (unit) => {
    if (unit === null) {
      setLocalUnit(null);
    } else {
      setLocalUnit({
        code: Number(unit.code),
        display_name: unit.display_name,
        description: unit.description,
      });
    }
  };

  const showUnitOri18n = () =>
    (unit && unit.display_name) || texts.timeSeriesDataUnitLabel;

  const handleOnSave = () => {
    if (localName.trim() === "") {
      warningSnackbar(texts.warningNoEmptyNodeName);
    } else {
      saveNodeInTile();
      saveNodeDataAndCloseDataPanel(
        nodeId,
        localName,
        localDataAcquisitionType,
        localPollingIntervalTime,
        localUnit,
      );
    }
  };
  useEffect(() => {
    if (prevLocalNameRef.current !== localName) {
      setGraphOptionsObject(undefined);
      setLastKnownValueAndTimestamp(undefined);
      fetchTimeseriesData(
        localName,
        nodeId,
        showUnitOri18n(),
        true,
        setLastKnownValueAndTimestamp,
        setTimeseriesDataType,
        setTsid,
        setGraphOptionsObject,
        setStringDataAndTimestampData,
      );
      prevLocalNameRef.current = localName;
    }
  }, [localName, nodeId, unit, key]);

  useEffect(() => {
    // setting some states to undefined to trigger "Loading..." message
    setTsid(undefined);
    setTimeseriesDataType(undefined);
    setLocalUnit(unit);
    prevLocalNameRef.current = null;
    setLocalName(name);
    setLocalDataAcquisitionType(dataAcquisitionType);
    setLocalPollingIntervalTime(smallestSampleIntervall);
  }, [key, name, dataAcquisitionType, smallestSampleIntervall]);

  return (
    <div className={styles[cssClassName]}>
      <div className={styles[`${cssClassName}__content`]}>
        {/* Name - haben wir */}
        {nodeSelectionNodeState === "show" && displayName(name)}
        {nodeSelectionNodeState === "edit" && displayEditName(localName)}
        {/* description - haben wir nicht */}
        <DisplayDataAcquisitionTypeItemsWrapper
          render={nodeSelectionNodeState === "edit"}
        />

        <LastActualValueAndTimestampIfStringOrNumeric
          timeseriesDataType={timeseriesDataType}
          lastKnownValueAndTimestamp={lastKnownValueAndTimestamp}
        />

        {/* this should remain UNRELEASED, is only for internal use */}
        <RenderUnreleasedFeatures>
          <LastActualValueAndTimestampIfJson
            timeseriesDataType={timeseriesDataType}
            lastKnownValueAndTimestamp={lastKnownValueAndTimestamp}
          />
        </RenderUnreleasedFeatures>

        {/* rank - haben wir nicht */}
        {/* entity - haben wir nicht */}
        {/* smallest sample intervall - sample_interval_in_seconds */}
        <AttributeNameValue
          name={texts.smallestSampleIntervalLabel}
          value={smallestSampleIntervallInSeconds}
        />

        {/* array dimensions - habe wir nicht */}
        <TimeSeriesDataType dataType={timeseriesDataType} />
        {nodeSelectionNodeState === "show" && displayUnitOrNothing(unit)}
        {nodeSelectionNodeState === "edit" && displayInputField()}

        <>
          {nodeId && !stringDataAndTimestampData && (
            <TimeSeriesLineGraphWrapper
              labelText={texts.timeSeriesDataLabelText}
              graphOptionsObject={graphOptionsObject}
            />
          )}
        </>

        {/* <p>{JSON.stringify(stringDataAndTimestampData, null, 2)}</p> */}
        {stringDataAndTimestampData && (
          <TimestampsAndStringsDataTable data={stringDataAndTimestampData} />
        )}

        <NodeIdBlock
          copyFunction={copyNodeIdToClipBoard}
          id={nodeId}
          label={texts.nodeIdBlock.child.label}
          tooltip={nodeId}
          tooltipPosition="top"
          copyTooltip={texts.nodeIdBlock.tooltip}
          copyTooltipPosition="left"
        />

        {parentNodeId && nodeSelectionNodeState === "show" && (
          <NodeIdBlock
            copyFunction={copyParentNodeIdToClipBoard}
            id={parentNodeId}
            label={texts.nodeIdBlock.parent.label}
            tooltip={nodeId}
            tooltipPosition="top"
            copyTooltip={texts.nodeIdBlock.tooltip}
            copyTooltipPosition="left"
          />
        )}

        {nodeSelectionNodeState === "edit" && displayAncestorsLineage()}
        <TsidContainer tsid={tsid} />

        <RenderIfUserHasPermission permission={"manage-blueprint-dashboards"}>
          {nodeSelectionNodeState === "edit" && localTiles.length > 0 && (
            <>
              <label className={styles[`${cssClassName}__label`]}>
                {texts.selectTileLabel}
                <select
                  value={localTileId}
                  onChange={(e) => handleChangeLocalTileId(e.target.value)}
                  className={styles[`${cssClassName}__dropdown`]}
                >
                  {localTiles.map((tile) => (
                    <option key={tile.getId()} value={tile.id}>
                      {tile.title}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles[`${cssClassName}__label`]}>
                {texts.selectChartTypeLabel}
                <select
                  value={chartType}
                  onChange={(e) => handleChangeChartType(e.target.value)}
                  className={styles[`${cssClassName}__dropdown`]}
                >
                  {chartTypes.map((chartType) => (
                    <option key={chartType} value={chartType}>
                      {chartType}
                    </option>
                  ))}
                </select>
              </label>
              {/* TODO: reactivate if needed or automate if information should be taken from directly from node */}
              {/* <label className={styles[`${cssClassName}__label`]}>
                Select node type
                <select
                  value={nodeType}
                  onChange={(e) => handleChangeNodeType(e.target.value)}
                  className={styles[`${cssClassName}__dropdown`]}
                >
                  {VALID_NODE_TYPES.map((nodeType) => (
                    <option key={nodeType} value={nodeType}>
                      {nodeType}
                    </option>
                  ))}
                </select>
              </label> */}
            </>
          )}
        </RenderIfUserHasPermission>

        {nodeSelectionNodeState === "edit" && (
          <div className={styles[`${cssClassName}__button-wrapper`]}>
            <Button fun={handleOnSave} text={texts.saveButtonText} />
            <Button
              fun={closeNodeDataPanel}
              text={texts.cancelButtonText}
              variant="secondary"
            />
            <Button
              fun={() => removeNodeAndCloseDataPanel()}
              text={texts.removeButtonText}
              variant={["secondary", "alert"]}
            />
          </div>
        )}
      </div>
    </div>
  );
};
